// Help: how to send, past notes and what happened to them, and one button to send a new note.

import { useState } from 'preact/hooks';
import { api } from '../lib/api';
import { Button, Empty, LoadError, Spinner, useLoad } from '../lib/ui';
import { Icon } from '../lib/icons';
import { ago } from '../lib/format';
import { ReportProblem } from '../app';
import type { Ticket } from '../../shared/types';

export function Help() {
  const t = useLoad(() => api<{ tickets: Ticket[] }>('/api/tickets').then((r) => r.tickets), []);
  const [open, setOpen] = useState(false);
  const mine = (t.data || []).filter((x) => x.source === 'jen' || x.status === 'closed');
  const auto = (t.data || []).filter((x) => x.source === 'app' && x.status === 'open');

  return (
    <div class="wrap narrow-l">
      <div class="page-head">
        <div>
          <h1 class="display">Help</h1>
          <p class="lead">Problems and ideas come straight to us. A person reads every one.</p>
        </div>
        <div class="head-actions">
          <Button icon="chat" onClick={() => setOpen(true)}>
            Send a note
          </Button>
        </div>
      </div>

      <section class="card how-to">
        <h2 class="card-title">How to send an email</h2>
        <ol class="how-steps">
          <li>
            Tap <b>Pick a ready email</b> and choose one.
          </li>
          <li>Tap any words to change them.</li>
          <li>
            Tap <b>Next</b> to check and send.
          </li>
        </ol>
      </section>

      {auto.length > 0 && (
        <section class="card">
          <h2 class="card-title">The app noticed</h2>
          <ul class="tickets">
            {auto.map((x) => (
              <TicketRow key={x.id} t={x} />
            ))}
          </ul>
        </section>
      )}

      <section class="card">
        <h2 class="card-title">Your notes</h2>
        {t.error ? (
          <LoadError error={t.error} retry={t.reload} />
        ) : !t.data ? (
          <Spinner />
        ) : mine.length === 0 ? (
          <Empty icon="bulb" title="Nothing yet">
            Tap Help at the top of any page.
          </Empty>
        ) : (
          <ul class="tickets">
            {mine.map((x) => (
              <TicketRow key={x.id} t={x} />
            ))}
          </ul>
        )}
      </section>

      <section class="card tips">
        <h2 class="card-title">Good to know</h2>
        <ul>
          <li>You can close the app while an email goes out.</li>
          <li>If too many emails bounce, sending pauses itself and tells you why.</li>
        </ul>
      </section>

      {open && (
        <ReportProblem
          onClose={() => {
            setOpen(false);
            t.reload();
          }}
        />
      )}
    </div>
  );
}

function TicketRow({ t }: { t: Ticket }) {
  const fixed = t.status === 'closed';
  const idea = t.kind === 'idea';
  return (
    <li class={`ticket ${fixed ? 'fixed' : ''}`}>
      <span class={`ticket-ic ${fixed ? 'ok' : idea ? 'idea' : ''}`}>
        <Icon name={fixed ? 'check' : idea ? 'bulb' : 'clock'} size={16} />
      </span>
      <div class="grow">
        <b>{t.title}</b>
        <small class="muted">
          {fixed ? `${idea ? 'Done' : 'Fixed'} ${ago(t.closed_at)}` : `${ago(t.created_at)} · ${idea ? 'Got it' : "We're on it"}`}
        </small>
        {fixed && t.fix_note && <p class="fix-note">{t.fix_note}</p>}
      </div>
    </li>
  );
}
